import { useState, type ChangeEvent } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faUpload } from "@fortawesome/free-solid-svg-icons";

// A new file to be attached to a resource. Includes the File object to permit uploading.
export interface UploadFile {
  file: File;
  previewUrl: string;
  hasError: boolean;
}

// An existing file attached to a resource.
export interface DocumentFile {
  preview: string,
  id: string,
  type: string,
}

// The Upload component forwards several properties into the input element. Here we pick the ones we use.
type UploadInputElementProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
type ForwardedInputElementProps = Pick<UploadInputElementProps, "disabled" | "className" | "id" | "name" | "value" | "disabled" | "readOnly" | "required" | "accept" | "multiple" | "onClick">

interface UploadProps extends ForwardedInputElementProps {
  camera: boolean;
  label?: string;
  files: UploadFile[];
  setFiles: (files: UploadFile[]) => void;
  documents: DocumentFile[]; // Included to determine whether adding further files to a resource will violate size and file count rules. 
}

function Upload(props: UploadProps) {
  const [error, setError] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const maxFileSizeBytes = 5_000_000;
  const maxFilesUploaded = 5;

  let classProps = ["upload__wrap"];

  if (props.disabled) {
    classProps.push("upload__wrap--disabled");
  }

  if (props.className) {
    classProps.push(props.className);
  }

  let inputProps: UploadInputElementProps = {
    type: "file",
    className: "upload",
    id: props.id ? props.id : props.name,
    name: props.name,
  };

  if (props.value) {
    inputProps.value = props.value;
  }

  if (props.disabled) {
    inputProps.disabled = props.disabled;
  }

  if (props.readOnly) {
    inputProps.readOnly = props.readOnly;
  }

  if (props.disabled) {
    inputProps.readOnly = true;
  }

  if (props.required) {
    inputProps.required = props.required;
  }

  if (props.accept) {
    inputProps.accept = props.accept;
  }

  if (props.camera) {
    inputProps.capture = true;
  }

  if (!props.disabled) {
    inputProps.onChange = handleInputChange;
  }

  if (props.multiple) {
    inputProps.multiple = props.multiple;
  }

  if (props.onClick) {
    inputProps.onClick = props.onClick;
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target?.files === null) {
      // Only null in a non type="file" input element.
      console.error("Unexpected null files in file upload handler.")
      return;
    }

    if (!props.multiple) {
      let file = e.target.files[0];
      if (file === undefined) {
        // TODO - Handle undefined file?
        return;
      }

      const uploadFile = { file: file, hasError: false, previewUrl: URL.createObjectURL(file) };
      props.setFiles([uploadFile]);
      checkFileType(uploadFile);

      return;
    }

    let newFiles: UploadFile[] = [];

    var newFileCount = e.target.files.length;
    var existingFileCount = (props.files?.length ?? 0) + (props.documents?.length ?? 0);
    var totalFiles = newFileCount + existingFileCount;

    if (totalFiles > maxFilesUploaded) {
      setError(true);
      setErrorMsg(`Cannot upload more than ${maxFilesUploaded} files`);
      return;
    }

    const existingFileNames = props.files.map(f => f.file.name);

    for (var i = 0; i < e.target.files.length; i++) {
      var file = e.target.files[i];
      if (file === undefined) {
        continue;
      }

      // Ignore files which have already been added, by name
      if (existingFileNames.indexOf(file.name) !== -1) {
        continue;
      }

      if (file.size > maxFileSizeBytes) {
        setError(true);
        setErrorMsg(`Maximum file size is 5MB`);
        break;
      }

      if (file.type !== "image/jpeg" && file.type !== "image/png") {
        setError(true);
        setErrorMsg("Please make sure the image is of a jpeg or png");
        break;
      }

      setError(false);
      newFiles.push({ file: file, hasError: false, previewUrl: URL.createObjectURL(file) });
    }

    // Append new files to the existing files
    const files = [...props.files, ...newFiles];
    props.setFiles(files);
  }

  function checkFileType(item: UploadFile) {
    console.log(item);
    if (item.file.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
      setError(true)
      setErrorMsg("Please make sure the file is Microsoft Excel file (.xlsx)")
    } else {
      setError(false);
      setErrorMsg(null);
    }
  }

  return (
    <div className={classProps.join(" ")} data-test-id="">
      <label
        className="upload__label"
        htmlFor={props.id ? props.id : props.name}
        style={
          props.disabled
            ? { pointerEvents: "none" }
            : { pointerEvents: "auto" }
        }
      >
        {props.camera ? (
          <FontAwesomeIcon icon={faCamera} className="upload__icon" />
        ) : (
          <FontAwesomeIcon icon={faUpload} className="upload__icon" />
        )}
        {props.label ? props.label : props.name}
        <input {...inputProps} />
      </label>

      {error ? <span className="input__error">{errorMsg}</span> : ""}
    </div>
  );
}

export default Upload;
